import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Empty,
  Modal,
  Segmented,
  Space,
  Spin,
  Table,
} from "antd";
import {
  ArrowLeftOutlined,
  AppstoreOutlined,
  BarsOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  LockOutlined,
  NotificationOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import {
  formatContractRef,
  formatServiceRequestRef,
  formatWorkorderRef,
} from "@/utils/entity-ref";
import AttachmentViewer from "@/components/AttachmentViewer";
import {
  CONTRACT_STATUS_COLORS,
  getContractSteps,
  getDisplayStatus,
  getDisplayStatusLabel,
} from "./constants";
import {
  ActionBar,
  ActionGroup,
  AmountValue,
  BackButton,
  CardGrid,
  ContractorIdentity,
  ContractorQuoteCard,
  ContractorQuoteGrid,
  ContractorQuoteHeader,
  DetailCard,
  DetailField,
  DetailFieldGrid,
  DetailLabel,
  DetailValue,
  DescriptionValue,
  Header,
  HeaderCopy,
  HeaderIdentity,
  HeaderTitle,
  LatestQuote,
  Page,
  QuoteHistory,
  QuoteMeta,
  QuoteRevision,
  RequestId,
  SectionHeading,
  StatusPill,
  StepConnector,
  StepDot,
  StepItem,
  StepLabel,
  Stepper,
  StepTrack,
  TenantRequestLink,
  WorkOrderLink,
} from "./ContractDetail.styles";
import {
  ContractNotesPanel,
  ContractLogsPanel,
  NOTE_CHANNEL_ANNOUNCEMENT,
  NOTE_CHANNEL_SUBMISSION,
  resolveNoteChannel,
  splitNotesByChannel,
} from "./ContractActivity";
import ContractNoteModal from "./ContractNoteModal";
import ContractCompanyDetailModal from "./ContractCompanyDetailModal";
import ContractCcEmailsSelect, { renderCcEmailsLabel } from "./ContractCcEmailsSelect";

const getTone = (status) => (CONTRACT_STATUS_COLORS[status] ? status : "closed");

const getInitials = (name) => {
  const value = String(name || "?").trim();
  if (!value) return "?";
  const parts = value.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : value.slice(0, 2))
    .toUpperCase();
};

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD HH:mm") : "-";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "-";
};

const formatQuoteDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "-";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const renderAttachment = (attachment) => (
  <span
    key={attachment.id}
    style={{ display: "inline-flex", alignItems: "center", marginTop: 8, marginRight: 12 }}
  >
    <AttachmentViewer attachment={attachment} />
  </span>
);

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.status ||
  error?.response?.data?.detail ||
  error?.response?.data?.assignTo ||
  Object.values(error?.response?.data || {})[0] ||
  fallback;

export default function ContractDetail({
  requestId,
  listPath,
  loadRequest,
  role,
  addNote,
  deleteNote,
  deleteNoteAttachment,
  canAddNote,
  updateCcEmails,
  canEditCcEmails,
  closeContract,
  cancel,
  reactivate,
  award,
  createWorkorder,
  getWorkorderPath,
  getTenantRequestPath,
}) {
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [closeSyncOpen, setCloseSyncOpen] = useState(false);
  const [quoteView, setQuoteView] = useState("cards");
  const [contractorOpen, setContractorOpen] = useState(false);
  const [ccEmailsOpen, setCcEmailsOpen] = useState(false);
  const [ccEmailsDraft, setCcEmailsDraft] = useState([]);
  const [savingCcEmails, setSavingCcEmails] = useState(false);

  const loadRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await loadRequest(requestId);
      setRecord(response?.data ?? response);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to load contract"));
    } finally {
      setLoading(false);
    }
  }, [loadRequest, requestId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const displayStatus = getDisplayStatus(record);
  const displayLabel = getDisplayStatusLabel(record);
  const progressSteps = getContractSteps(displayStatus);
  // This copy of the component only serves the facility / portfolio side; the
  // contractor portal lives in FacilityKPI-Contract.
  const availableActions = record?.available_actions || [];
  const noteEnabled = Boolean(canAddNote && availableActions.includes("add_note"));
  // Notes written here are always public announcements; contractors send
  // private submissions from the contractor portal instead.
  const canAddNoteIn = (channel) => noteEnabled && channel === NOTE_CHANNEL_ANNOUNCEMENT;
  const noteAddLabel = "Add Announcement";
  // Delete follows the backend permission story: the facility side (with
  // contract:change, which also gates canAddNote) may delete any note.
  const canDeleteNoteItem = () => Boolean(deleteNote && canAddNote);
  const noteNoun = (note) =>
    resolveNoteChannel(note) === NOTE_CHANNEL_SUBMISSION ? "submission" : "announcement";
  const confirmDeleteNote = (note) => {
    const noun = noteNoun(note);
    Modal.confirm({
      title: `Delete this ${noun}?`,
      content:
        "The text and all of its attachments will be removed. Email notifications that have already been sent cannot be recalled.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteNote(requestId, note.id);
          await loadRecord();
          message.success(`${noun === "submission" ? "Submission" : "Announcement"} deleted successfully`);
        } catch (error) {
          message.error(getErrorMessage(error, `Failed to delete ${noun}`));
        }
      },
    });
  };
  const confirmDeleteAttachment = (note, attachment) => {
    Modal.confirm({
      title: "Delete this attachment?",
      content: `"${attachment.original_name || "Attachment"}" will be removed.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteNoteAttachment(requestId, note.id, attachment.id);
          await loadRecord();
          message.success("Attachment deleted successfully");
        } catch (error) {
          message.error(getErrorMessage(error, "Failed to delete attachment"));
        }
      },
    });
  };
  // The CC list only feeds notification emails, so it stays editable after the
  // request is created (facility side only).
  const openCcEmailsModal = () => {
    setCcEmailsDraft(Array.isArray(record?.cc_emails) ? [...record.cc_emails] : []);
    setCcEmailsOpen(true);
  };
  const saveCcEmails = async () => {
    const emails = Array.from(
      new Set(
        (ccEmailsDraft || [])
          .map((item) => String(item).trim().toLowerCase())
          .filter(Boolean)
      )
    );
    const invalidEmail = emails.find((item) => !EMAIL_PATTERN.test(item));
    if (invalidEmail) {
      message.error(`Invalid email address: ${invalidEmail}`);
      return;
    }
    setSavingCcEmails(true);
    try {
      await updateCcEmails(requestId, emails);
      setCcEmailsOpen(false);
      await loadRecord();
      message.success("CC email addresses updated");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to update CC email addresses"));
    } finally {
      setSavingCcEmails(false);
    }
  };
  const canClose = Boolean(
    closeContract && availableActions.includes("close")
  );
  const canCancel = Boolean(
    cancel && availableActions.includes("cancel")
  );
  const canReactivate = Boolean(
    reactivate && availableActions.includes("reactivate")
  );
  const canCreateWorkorder = Boolean(
    createWorkorder && availableActions.includes("create_workorder")
  );

  // When closing a contract that is linked to a work order, offer to also sync
  // that work order's status to "Completed" (matches the status option shown on
  // the work-order create/edit forms).
  const workorderStatus = record?.workorder_status || null;
  const hasLinkedWorkorder = Boolean(record?.workorder_id);
  const workorderAlreadyCompleted = workorderStatus === "Completed";
  const shouldPromptWorkorderSync = Boolean(
    canClose && hasLinkedWorkorder && !workorderAlreadyCompleted && closeContract
  );

  const workorderLabel = record?.workorder_id
    ? record.workorder_title ||
      formatWorkorderRef(record.workorder_number ?? record.workorder_id)
    : "-";
  const tenantRequestLabel = record?.service_request_id
    ? record.service_request_title ||
      formatServiceRequestRef(record.service_request_id)
    : "-";
  const goToPath = (resolvePath) => {
    if (!resolvePath || !record) return;
    const path = resolvePath(record);
    if (path) navigate(path);
  };

  // One notes feed, rendered as two panels: Announcement (facility side, public
  // to every party on the request) and Submission (contractor, private to that
  // contractor plus the facility side). The backend decides the channel from
  // the author and only ever sends a viewer the notes they may see.
  const { announcements, submissions } = useMemo(
    () => splitNotesByChannel(record?.notes),
    [record?.notes]
  );

  // Files dropped straight into the contract's managed folders in /file-manage
  // (no backing note). The backend tags each with a `channel` and scopes
  // visibility; the Attachment tabs render them read-only - deletion happens
  // in /file-manage, same as any other file there.
  const directAnnouncementAttachments = useMemo(
    () => (record?.direct_attachments || []).filter((a) => a.channel !== "submission"),
    [record?.direct_attachments]
  );
  const directSubmissionAttachments = useMemo(
    () => (record?.direct_attachments || []).filter((a) => a.channel === "submission"),
    [record?.direct_attachments]
  );

  const contractorQuoteGroups = useMemo(() => {
    const groups = new Map();
    (record?.quotes || []).forEach((quote) => {
      const key = String(quote.contractor);
      if (!groups.has(key)) {
        groups.set(key, {
          contractorId: quote.contractor,
          contractorName: quote.contractor_name || "Unknown contractor",
          contractorEmail: quote.contractor_email,
          contractorLogo: quote.contractor_logo,
          quotes: [],
        });
      }
      groups.get(key).quotes.push(quote);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        quotes: group.quotes.sort(
          (a, b) => new Date(b.created_date) - new Date(a.created_date)
        ),
      }))
      .sort((a, b) => new Date(b.quotes[0].created_date) - new Date(a.quotes[0].created_date));
  }, [record?.quotes]);

  const submitNote = async (content, files) => {
    if (!content.replace(/<[^>]*>/g, "").trim() && !files.length) {
      message.error("Please enter an announcement");
      return;
    }
    setSubmittingNote(true);
    try {
      const body = new FormData();
      body.append("content", content || "<p>Attachment</p>");
      files.forEach((file) => body.append("attachments", file));
      // The channel is not part of the payload: the backend derives it from
      // whoever is writing (facility -> announcement, contractor -> submission).
      await addNote(requestId, body);
      setNoteModalOpen(false);
      await loadRecord();
      message.success("Announcement published successfully");
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to add announcement"));
    } finally {
      setSubmittingNote(false);
    }
  };

  const changeStatus = async (action, successText) => {
    if (!action || !record) return;
    setChangingStatus(true);
    try {
      await action(record.id);
      await loadRecord();
      message.success(successText);
    } catch (error) {
      message.error(getErrorMessage(error, "Failed to update contract status"));
    } finally {
      setChangingStatus(false);
    }
  };

  // Close the contract request, optionally syncing the linked work order to
  // "Completed". Closes the confirm dialog when the request finishes; on
  // failure the dialog stays open and shows the error.
  const runClose = (syncWorkorder) => {
    if (!closeContract || !record) return Promise.resolve();
    setChangingStatus(true);
    return closeContract(record.id, syncWorkorder)
      .then(() => loadRecord())
      .then(() => {
        setCloseSyncOpen(false);
        message.success("Contract closed");
      })
      .catch((error) => {
        message.error(getErrorMessage(error, "Failed to update contract status"));
        return Promise.reject(error);
      })
      .finally(() => setChangingStatus(false));
  };

  // Entry point for the Close button. Always show a confirm dialog first to
  // avoid accidental clicks. When the request is linked to a work order that is
  // not yet completed, an extra option lets the user sync that work order to
  // "Completed" at the same time.
  const handleCloseClick = () => {
    setCloseSyncOpen(true);
  };

  const currentIndex = Math.max(
    0,
    progressSteps.findIndex((step) => step.key === displayStatus)
  );

  const actionButtons = (
    <ActionGroup>
      {noteEnabled ? (
        <Button
          icon={<NotificationOutlined />}
          onClick={() => setNoteModalOpen(true)}
        >
          {noteAddLabel}
        </Button>
      ) : null}
      {canClose ? (
        <Button
          danger
          icon={<LockOutlined />}
          onClick={handleCloseClick}
          loading={changingStatus}
        >
          Close
        </Button>
      ) : null}
      {canCancel ? (
        <Button
          danger
          icon={<CloseCircleOutlined />}
          onClick={() => changeStatus(cancel, "Contract request cancelled")}
          loading={changingStatus}
        >
          Cancel
        </Button>
      ) : null}
      {canReactivate ? (
        <Button
          icon={<CheckCircleOutlined />}
          onClick={() => changeStatus(reactivate, "Contract request reactivated")}
          loading={changingStatus}
        >
          Reactivate
        </Button>
      ) : null}
      {canCreateWorkorder ? (
        <Button icon={<ToolOutlined />} onClick={() => createWorkorder(record)}>
          Create Work Order
        </Button>
      ) : null}
    </ActionGroup>
  );

  return (
    <Page>
      <Header>
        <HeaderIdentity>
          <BackButton icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)} aria-label="Back" />
          <HeaderCopy>
            <HeaderTitle level={4}>Contract Request {record ? formatContractRef(record.id) : ""}</HeaderTitle>
            {record ? <RequestId>{record.title}</RequestId> : null}
          </HeaderCopy>
          {record ? (
            <StatusPill $status={getTone(displayStatus)}>{displayLabel}</StatusPill>
          ) : null}
        </HeaderIdentity>
      </Header>

      <Spin spinning={loading}>
        {record ? (
          <>
            <Stepper aria-label="Contract progress">
              <StepTrack>
                {progressSteps.map((step, index) => {
                  const done = index < currentIndex;
                  const active = index === currentIndex;
                  return (
                    <StepItem key={step.key} $active={active} $done={done}>
                      <StepDot $active={active} $done={done}>
                        {done ? <CheckOutlined /> : index === 0 ? <FileTextOutlined /> : index === progressSteps.length - 1 ? <LockOutlined /> : <ToolOutlined />}
                      </StepDot>
                      <StepLabel $active={active} $done={done}>
                        {step.label}
                      </StepLabel>
                      {index < progressSteps.length - 1 ? (
                        <StepConnector $done={done} $active={active} />
                      ) : null}
                    </StepItem>
                  );
                })}
              </StepTrack>
            </Stepper>
            <ActionBar>{actionButtons}</ActionBar>

            <CardGrid>
              <DetailCard>
                <SectionHeading><FileTextOutlined /> Contract Information</SectionHeading>
                <DetailField>
                  <DetailLabel>Title / Issue</DetailLabel>
                  <DetailValue>{record.title || "-"}</DetailValue>
                </DetailField>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Facility</DetailLabel>
                    <DetailValue>{record.facility_name || record.facility || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Company</DetailLabel>
                    <DetailValue>{record.company_name || "-"}</DetailValue>
                  </DetailField>
                </DetailFieldGrid>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Work Order</DetailLabel>
                    <DetailValue>
                      {record.workorder_id ? (
                        getWorkorderPath ? (
                          <WorkOrderLink
                            type="link"
                            onClick={() => goToPath(getWorkorderPath)}
                          >
                            {workorderLabel}
                          </WorkOrderLink>
                        ) : (
                          workorderLabel
                        )
                      ) : "-"}
                    </DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Tenant Request</DetailLabel>
                    <DetailValue>
                      {record.service_request_id ? (
                        getTenantRequestPath ? (
                          <TenantRequestLink
                            type="link"
                            onClick={() => goToPath(getTenantRequestPath)}
                          >
                            {tenantRequestLabel}
                          </TenantRequestLink>
                        ) : (
                          tenantRequestLabel
                        )
                      ) : "-"}
                    </DetailValue>
                  </DetailField>
                </DetailFieldGrid>
                {role !== "contract" ? (
                  <DetailField>
                    <DetailLabel>
                      <Space size={8}>
                        <span>CC Emails</span>
                        {canEditCcEmails && updateCcEmails ? (
                          <a
                            href="#edit-cc-emails"
                            onClick={(event) => {
                              event.preventDefault();
                              openCcEmailsModal();
                            }}
                            style={{
                              color: "#1677ff",
                              textDecoration: "underline",
                              textTransform: "none",
                            }}
                          >
                            Edit
                          </a>
                        ) : null}
                      </Space>
                    </DetailLabel>
                    <DetailValue>
                      {Array.isArray(record.cc_emails) && record.cc_emails.length
                        ? record.cc_emails.join(", ")
                        : "-"}
                    </DetailValue>
                  </DetailField>
                ) : null}
              </DetailCard>

              <DetailCard>
                <SectionHeading><ToolOutlined /> Assignment & Dates</SectionHeading>
                <DetailField>
                  <DetailLabel>Assignee</DetailLabel>
                  {record.assignTo_name ? (
                    <Space size={8}>
                      <Avatar size={28} src={record.assignTo_logo}>{getInitials(record.assignTo_name)}</Avatar>
                      {record.assignTo != null && role === "facility" ? (
                        <span>
                          <a
                            href="#contractor-details"
                            onClick={(event) => { event.preventDefault(); setContractorOpen(true); }}
                            style={{ color: "#1677ff", textDecoration: "underline" }}
                          >
                            {record.assignTo_name}
                          </a>
                          {record.assignTo_email ? <small style={{ display: "block" }}>{record.assignTo_email}</small> : null}
                        </span>
                      ) : (
                        <span><span style={{ display: "block" }}>{record.assignTo_name}</span>{record.assignTo_email ? <small>{record.assignTo_email}</small> : null}</span>
                      )}
                    </Space>
                  ) : <DetailValue>Unclaimed</DetailValue>}
                </DetailField>
                <DetailFieldGrid>
                  <DetailField>
                    <DetailLabel>Equipment</DetailLabel>
                    <DetailValue>{record.equipment_name || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Priority</DetailLabel>
                    <DetailValue $priority={record.priority}>{record.priority || "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Work Due Date</DetailLabel>
                    <DetailValue>{record.due_date ? formatDate(record.due_date) : "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Bid Due Date</DetailLabel>
                    <DetailValue>{record.bid_due_date ? formatDate(record.bid_due_date) : "-"}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Created</DetailLabel>
                    <DetailValue>{formatDate(record.created_date)}</DetailValue>
                  </DetailField>
                  <DetailField>
                    <DetailLabel>Last Updated</DetailLabel>
                    <DetailValue>{formatDate(record.modified_date || record.created_date)}</DetailValue>
                  </DetailField>
                </DetailFieldGrid>
              </DetailCard>

              {record.awarded_amount != null ? (
                <DetailCard $full>
                  <SectionHeading><DollarOutlined /> Awarded Amount</SectionHeading>
                  <DetailField>
                    <DetailLabel>Winning Bid Amount</DetailLabel>
                    <AmountValue style={{ color: "#16a34a" }}>
                      {formatMoney(record.awarded_amount)}
                    </AmountValue>
                  </DetailField>
                  {record.assignTo_name ? (
                    <QuoteMeta style={{ marginTop: 4 }}>
                      Awarded to {record.assignTo_name}
                    </QuoteMeta>
                  ) : null}
                </DetailCard>
              ) : null}

              <DetailCard $full>
                <SectionHeading><FileTextOutlined /> Description</SectionHeading>
                <DescriptionValue>{record.description || "No description provided."}</DescriptionValue>
              </DetailCard>

              {/* Announcement sits right below Description: it is the facility's
                  public briefing for the request, so it reads best before the
                  commercial sections. The panel's own tabs (Announcement /
                  Attachment) already name the section, so the card carries no
                  heading. */}
              <DetailCard $full>
                <ContractNotesPanel
                  notes={announcements}
                  canAddNote={canAddNoteIn(NOTE_CHANNEL_ANNOUNCEMENT)}
                  onAddNote={() => setNoteModalOpen(true)}
                  addLabel="Add Announcement"
                  emptyText="No announcements yet"
                  textTabLabel="Announcement"
                  canDeleteNote={canDeleteNoteItem}
                  onDeleteNote={deleteNote ? confirmDeleteNote : undefined}
                  onDeleteAttachment={deleteNoteAttachment ? confirmDeleteAttachment : undefined}
                  directAttachments={directAnnouncementAttachments}
                />
              </DetailCard>

              {record.total_cost !== null && record.total_cost !== undefined ? (
                <DetailCard $full>
                  <SectionHeading><FileTextOutlined /> Cost</SectionHeading>
                  <DetailField>
                    <DetailLabel>Total Cost</DetailLabel>
                    <AmountValue>{formatMoney(record.total_cost)}</AmountValue>
                  </DetailField>
                </DetailCard>
              ) : null}

              <DetailCard $full>
                <SectionHeading $spread>
                  <span><DollarOutlined /> Contractor Quotes</span>
                  {(record.quotes || []).length ? (
                    <Segmented
                      size="small"
                      value={quoteView}
                      onChange={setQuoteView}
                      options={[
                        { value: "cards", icon: <AppstoreOutlined />, label: "Cards" },
                        { value: "list", icon: <BarsOutlined />, label: "List" },
                      ]}
                    />
                  ) : null}
                </SectionHeading>
                {(record.quotes || []).length ? (
                  quoteView === "cards" ? (
                    <>
                      <ContractorQuoteGrid>
                        {contractorQuoteGroups.map((group) => {
                          const latestQuote = group.quotes[0];
                          const previousQuotes = group.quotes.slice(1);
                          const isAwarded = String(record.assignTo) === String(group.contractorId);
                          return (
                            <ContractorQuoteCard key={group.contractorId} $awarded={isAwarded}>
                              <ContractorQuoteHeader>
                                <ContractorIdentity>
                                  <Avatar size={40} src={group.contractorLogo}>{getInitials(group.contractorName)}</Avatar>
                                  <div>
                                    <strong>{group.contractorName}</strong>
                                    {group.contractorEmail ? <QuoteMeta>{group.contractorEmail}</QuoteMeta> : null}
                                    <QuoteMeta>
                                      {group.quotes.length} {group.quotes.length === 1 ? "quote" : "quotes"}
                                      {isAwarded ? " · Awarded contractor" : ""}
                                    </QuoteMeta>
                                  </div>
                                </ContractorIdentity>
                                {availableActions.includes("award") && award ? (
                                  <Button
                                    type="primary"
                                    size="small"
                                    loading={changingStatus}
                                    onClick={() => Modal.confirm({
                                      title: `Award contract to ${group.contractorName}?`,
                                      content: `The latest quote of ${formatMoney(latestQuote.amount)} will be selected.`,
                                      okText: "Award Contractor",
                                      onOk: () => changeStatus(
                                        () => award(record.id, group.contractorId),
                                        `Contract awarded to ${group.contractorName}`
                                      ),
                                    })}
                                  >
                                    Award Contractor
                                  </Button>
                                ) : null}
                              </ContractorQuoteHeader>
                              <LatestQuote $awarded={isAwarded}>
                                <QuoteMeta>Latest quote - {formatDate(latestQuote.created_date)}</QuoteMeta>
                                <AmountValue>{formatMoney(latestQuote.amount)}</AmountValue>
                                {latestQuote.start_time ? <QuoteMeta>Schedule: {formatQuoteDate(latestQuote.start_time)} - {formatQuoteDate(latestQuote.end_time)}</QuoteMeta> : null}
                                {latestQuote.description ? <p style={{ margin: "8px 0 0" }}>{latestQuote.description}</p> : null}
                                {(latestQuote.attachments || []).map(renderAttachment)}
                              </LatestQuote>
                              {previousQuotes.length ? (
                                <QuoteHistory>
                                  <summary>View {previousQuotes.length} previous {previousQuotes.length === 1 ? "quote" : "quotes"}</summary>
                                  {previousQuotes.map((quote) => (
                                    <QuoteRevision key={quote.id}>
                                      <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
                                        <QuoteMeta>{formatDate(quote.created_date)}</QuoteMeta>
                                        <strong>{formatMoney(quote.amount)}</strong>
                                      </Space>
                                      {quote.start_time ? <QuoteMeta>Schedule: {formatQuoteDate(quote.start_time)} - {formatQuoteDate(quote.end_time)}</QuoteMeta> : null}
                                      {quote.description ? <p style={{ margin: "6px 0 0" }}>{quote.description}</p> : null}
                                      {(quote.attachments || []).map(renderAttachment)}
                                    </QuoteRevision>
                                  ))}
                                </QuoteHistory>
                              ) : null}
                            </ContractorQuoteCard>
                          );
                        })}
                      </ContractorQuoteGrid>
                    </>
                  ) : (
                    <Table
                      rowKey="id"
                      size="small"
                      bordered
                      pagination={false}
                      scroll={{ x: 900 }}
                      dataSource={record.quotes}
                      columns={[
                        {
                          title: "Contractor",
                          key: "contractor",
                          width: 220,
                          render: (_, quote) => (
                            <div>
                              <strong>{quote.contractor_name || "Unknown contractor"}</strong>
                              {quote.contractor_email ? <QuoteMeta>{quote.contractor_email}</QuoteMeta> : null}
                            </div>
                          ),
                        },
                        {
                          title: "Submitted",
                          dataIndex: "created_date",
                          key: "created_date",
                          width: 160,
                          render: formatDate,
                        },
                        {
                          title: "Amount",
                          dataIndex: "amount",
                          key: "amount",
                          width: 120,
                          render: (amount) => <AmountValue>{formatMoney(amount)}</AmountValue>,
                        },
                        {
                          title: "Schedule",
                          key: "schedule",
                          width: 190,
                          render: (_, quote) => (
                            quote.start_time
                              ? `${formatQuoteDate(quote.start_time)} - ${formatQuoteDate(quote.end_time)}`
                              : "-"
                          ),
                        },
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: 260,
                          render: (description) => (
                            <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                              {description || "-"}
                            </span>
                          ),
                        },
                        {
                          title: "Attachments",
                          key: "attachments",
                          width: 180,
                          render: (_, quote) => (quote.attachments || []).length ? (
                            <Space wrap size={[4, 4]}>
                              {quote.attachments.map(renderAttachment)}
                            </Space>
                          ) : "-",
                        },
                      ]}
                    />
                  )
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No quotes submitted" />
                )}
              </DetailCard>

              {/* Submissions: private to the writing contractor plus the
                  facility side. Same story as above - the tabs name it. */}
              <DetailCard $full>
                <ContractNotesPanel
                  notes={submissions}
                  canAddNote={canAddNoteIn(NOTE_CHANNEL_SUBMISSION)}
                  onAddNote={() => setNoteModalOpen(true)}
                  addLabel="Add Submission"
                  emptyText="No submissions yet"
                  textTabLabel="Submission"
                  canDeleteNote={canDeleteNoteItem}
                  onDeleteNote={deleteNote ? confirmDeleteNote : undefined}
                  onDeleteAttachment={deleteNoteAttachment ? confirmDeleteAttachment : undefined}
                  directAttachments={directSubmissionAttachments}
                />
              </DetailCard>
              <DetailCard $full>
                <SectionHeading>
                  <span><HistoryOutlined /> Logs</span>
                </SectionHeading>
                <ContractLogsPanel logs={record?.logs} />
              </DetailCard>
            </CardGrid>
          </>
        ) : null}
      </Spin>

      <ContractNoteModal
        open={noteModalOpen}
        loading={submittingNote}
        title={noteAddLabel}
        onCancel={() => setNoteModalOpen(false)}
        onSubmit={submitNote}
      />
      {/* Facility + portfolio may maintain the CC list; the contractor portal
          (a separate copy of this component) must never see or edit it. */}
      {role !== "contract" ? (
        <Modal
          title="CC email addresses"
          open={ccEmailsOpen}
          onCancel={() => setCcEmailsOpen(false)}
          onOk={saveCcEmails}
          okText="Save"
          confirmLoading={savingCcEmails}
          destroyOnClose
        >
          <div style={{ marginBottom: 6 }}>{renderCcEmailsLabel("Email addresses")}</div>
          <ContractCcEmailsSelect
            value={ccEmailsDraft}
            onChange={setCcEmailsDraft}
            autoFocus
          />
          <div style={{ marginTop: 8, color: "#8c8c8c", fontSize: 12 }}>
            These addresses are copied on every notification email for this contract
            request that the creator receives. Leave the list empty to stop copying.
          </div>
        </Modal>
      ) : null}
      {role === "facility" ? (
        <ContractCompanyDetailModal
          open={contractorOpen}
          onClose={() => setContractorOpen(false)}
          contractor={
            record?.assignTo != null
              ? {
                  id: record.assignTo,
                  name: record.assignTo_name,
                  email: record.assignTo_email,
                  logo: record.assignTo_logo,
                }
              : null
          }
          facilityId={record?.facility || record?.facility_id}
        />
      ) : null}
      <Modal
        title={shouldPromptWorkorderSync ? "Close contract request?" : "Close this contract request?"}
        open={closeSyncOpen}
        onCancel={() => setCloseSyncOpen(false)}
        maskClosable
        keyboard
        closable
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setCloseSyncOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="close-only"
            danger
            loading={changingStatus}
            onClick={() => runClose(false)}
          >
            Close request
          </Button>,
          ...(shouldPromptWorkorderSync
            ? [
                <Button
                  key="close-sync"
                  type="primary"
                  loading={changingStatus}
                  onClick={() => runClose(true)}
                >
                  Close &amp; complete work order
                </Button>,
              ]
            : []),
        ]}
      >
        {shouldPromptWorkorderSync ? (
          <div>
            This request is linked to work order{" "}
            <strong>{workorderLabel}</strong>.
            <br />
            <br />
            Do you also want to set the linked work order status to{" "}
            <strong>Completed</strong>?
          </div>
        ) : (
          <div>Are you sure you want to close this contract request?</div>
        )}
      </Modal>
    </Page>
  );
}
